import axios from 'axios';
import { API_BASE_URL } from './config';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ===== Sesión expirada =====
// El access token dura 8 h. Ante un 401 se intenta renovarlo una sola vez con el refresh token
// y se repite la petición; si no se puede, se cierra la sesión y se va al login.

const RUTAS_SIN_RENOVAR = ['/api/authentication/login/', '/api/authentication/token/refresh/'];
let renovacionEnCurso = null;

const cerrarSesion = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  if (window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
};

// Devuelve el nuevo access token o null. Varias peticiones con 401 comparten la misma renovación.
export const renovarToken = () => {
  const refresh = localStorage.getItem('refresh_token');
  if (!refresh) return Promise.resolve(null);
  if (!renovacionEnCurso) {
    renovacionEnCurso = axios
      .post(`${API_BASE_URL}/api/authentication/token/refresh/`, { refresh })
      .then(({ data }) => {
        localStorage.setItem('token', data.access);
        if (data.refresh) localStorage.setItem('refresh_token', data.refresh);
        return data.access;
      })
      .catch(() => null)
      .finally(() => { renovacionEnCurso = null; });
  }
  return renovacionEnCurso;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const url = config?.url || '';
    if (error.response?.status !== 401 || !config || config._reintento || RUTAS_SIN_RENOVAR.some(r => url.includes(r))) {
      return Promise.reject(error);
    }
    if (!localStorage.getItem('token')) return Promise.reject(error);

    config._reintento = true;
    const nuevo = await renovarToken();
    if (!nuevo) {
      cerrarSesion();
      return Promise.reject(error);
    }
    config.headers.Authorization = `Bearer ${nuevo}`;
    return api(config);
  }
);

// Para pantallas que revisan `ok` como hacían con fetch(): no lanza ante 4xx/5xx (devuelve ok: false
// con el cuerpo de la respuesta) y sí lanza si no hubo respuesta, por ejemplo sin conexión.
// Envío de archivos: axios convertiría un FormData en JSON si se queda la cabecera por defecto.
export const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 };

export const pedir = async (method, url, data, config = {}) => {
  try {
    const respuesta = await api.request({ method, url, data, ...config });
    return { ok: true, status: respuesta.status, data: respuesta.data };
  } catch (error) {
    if (!error.response) throw error;
    return { ok: false, status: error.response.status, data: error.response.data };
  }
};

// Varios módulos llamaban a la API con fetch() y leían el token a mano. Este envoltorio les da
// el mismo comportamiento: si responde 401, renueva el token y repite la petición una vez.
export const instalarRenovacionEnFetch = () => {
  if (window.__fetchConRenovacion) return;
  window.__fetchConRenovacion = true;
  const fetchOriginal = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const response = await fetchOriginal(input, init);
    const url = typeof input === 'string' ? input : input?.url || '';
    const headers = new Headers(init.headers || (typeof input === 'object' ? input.headers : undefined));
    const auth = headers.get('Authorization') || '';

    if (response.status !== 401 || !auth.startsWith('Bearer ') || !url.includes('/api/') || RUTAS_SIN_RENOVAR.some(r => url.includes(r))) {
      return response;
    }
    const nuevo = await renovarToken();
    if (!nuevo) {
      cerrarSesion();
      return response;
    }
    headers.set('Authorization', `Bearer ${nuevo}`);
    return fetchOriginal(input, { ...init, headers });
  };
};

export default api;
